import { useEffect, useState } from 'react';
import { getQueuedCount, setupOfflineSync, isOnline } from '../lib/offlineCheckin';

export default function OfflineSyncStatus() {
  const [queueCount, setQueueCount] = useState(getQueuedCount());
  const [online, setOnline] = useState(isOnline());
  const [lastSyncResult, setLastSyncResult] = useState<{
    success: number;
    failed: number;
  } | null>(null);

  useEffect(() => {

    // Setup online/offline listeners
    const handleOnline = () => {
      setOnline(true);
    };

    const handleOffline = () => {
      setOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Setup offline sync with callback
    const cleanup = setupOfflineSync((result) => {
      setLastSyncResult(result);
      setQueueCount(getQueuedCount());
      
      // Clear sync result after 5 seconds
      setTimeout(() => {
        setLastSyncResult(null);
      }, 5000);
    });

    // Poll for queue count changes (in case items are added)
    const interval = setInterval(() => {
      setQueueCount(getQueuedCount());
    }, 2000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      cleanup();
      clearInterval(interval);
    };
  }, []);

  // Don't show anything if online and no queued items
  if (online && queueCount === 0 && !lastSyncResult) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Offline Indicator with Queue Count */}
      {!online && queueCount > 0 && (
        <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-[var(--radius-md)] px-4 py-3 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📱</span>
            <div>
              <p className="text-sm font-semibold text-yellow-400">Offline Mode</p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {queueCount} check-in{queueCount !== 1 ? 's' : ''} queued
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Success Notification */}
      {lastSyncResult && lastSyncResult.success > 0 && (
        <div className="bg-green-500/20 border border-green-500/40 rounded-[var(--radius-md)] px-4 py-3 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✓</span>
            <div>
              <p className="text-sm font-semibold text-green-400">Synced!</p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {lastSyncResult.success} check-in{lastSyncResult.success !== 1 ? 's' : ''} uploaded
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Failure Notification */}
      {lastSyncResult && lastSyncResult.failed > 0 && (
        <div className="bg-red-500/20 border border-red-500/40 rounded-[var(--radius-md)] px-4 py-3 shadow-lg backdrop-blur-sm mt-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✗</span>
            <div>
              <p className="text-sm font-semibold text-red-400">Sync Failed</p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                {lastSyncResult.failed} check-in{lastSyncResult.failed !== 1 ? 's' : ''} couldn't sync
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
